import _ from 'lodash';

import bills from '../../api/bills';
import * as types from '../mutation-types';
import moment from 'moment';

const DATE_FORMAT = 'YYYY-MM-DD';

const defaultFields = [
  'id',
  'created_at',
  'customer.name',
  'customer.contact_name',
  'customer.referrer',
  'provider_reference',
  'date_funding_obtained',
  'expected_fee',
  'fee_percentage',
  'funding_obtained',
  'fee_type',
  'bills.id',
  'bills.type',
  'bills.registered_company_to_bill',
  'bills.drawdown',
  'bills.fee',
  'bills.type_of_fee',
  'bills.status',
  'bills.comments',
  'bills.bill_date',
  'bills.received_date',
  'bills.created_at',
  'product.name',
  'product.registered_company_name',
  'product.provider.job_code',
  'product.provider.name',
  'product.provider.referral_credit_paid',
  'product.provider.registered_company_name'
];

const state = {
  billable: {
    isLoading: false,
    enquiries: [],
    currentPage: 1,
    pageSize: 15,
    lastPage: 1,
    total: 0,
    orderBy: 'date_funding_obtained',
    orderDirection: 'desc',
    filters: {
      status: 'new',
      dates: {
        column: 'created_at',
        from: '2016-05-01',
        to: moment().format(DATE_FORMAT)
      }
    },
    search: null
  },
  comments: []
};

const getters = {
  BillableEnquiries: state => state.billable.enquiries,
  BillableEnquiriesLoading: state => state.billable.isLoading,
  BillableFilters: state => state.billable.filters,
  BillableSearch: state => state.billable.search,
  BillableCurrentPage: state => state.billable.currentPage,
  BillableLastPage: state => state.billable.lastPage,
  BillableTotal: state => state.billable.total,
  BillablePageSize: state => state.billable.pageSize,
  BillableSortBy: state => state.billable.orderBy,
  BillableSortDesc: state => state.billable.orderDirection === 'desc'
};

const actions = {
  getBillableEnquiries({ commit, state }, resetPage = false) {
    const page = resetPage ? 1 : state.billable.currentPage;
    const orderBy = [{ [state.billable.orderBy]: state.billable.orderDirection }];
    const dates = state.billable.filters.dates;
    const filters = {};
    const terms = { state: ['Funded'] };

    if (state.billable.filters.status === 'new revolving') {
      filters[0] = '!billed';
    } else {
      terms['nested.bills.status'] = [state.billable.filters.status];
    }

    const ranges = [];
    const search = _.isNull(state.billable.search) ? '*' : `${state.billable.search}*`;
    const fields = defaultFields;

    commit(types.SET_BILLABLE_LOADING_STATE, true);

    bills
      .getPaginatedEnquiries(page, state.billable.pageSize, orderBy, terms, ranges, dates, fields, filters, search)
      .then(response => {
        commit(types.SET_BILLABLE_ENQUIRIES, response);
        commit(types.SET_BILLABLE_LOADING_STATE, false);
        if (resetPage) {
          commit(types.SET_BILLABLE_ENQUIRIES_CURRENT_PAGE, 1);
        }
      });
  },
  downloadBillableEnquiries({ state }) {
    const orderBy = [{ [state.billable.orderBy]: state.billable.orderDirection }];
    const dates = state.billable.filters.dates;
    const filters = {};
    const terms = { state: ['Funded'] };

    if (state.billable.filters.status === 'new revolving') {
      filters[0] = '!billed';
    } else {
      terms['nested.bills.status'] = [state.billable.filters.status];
    }

    const ranges = [];
    const search = _.isNull(state.billable.search) ? '*' : `${state.billable.search}*`;
    const fields = { ...defaultFields };

    return bills.downloadBillsCsv({ orderBy, terms, ranges, dates, fields, filters, search });
  },
  setBillableEnquiryFromDate({ commit }, from) {
    commit(types.SET_BILLABLE_ENQUIRY_FROM_DATE, from);
  },
  setBillableEnquiryToDate({ commit }, to) {
    commit(types.SET_BILLABLE_ENQUIRY_TO_DATE, to);
  },
  setBillableEnquiryDateColumn({ commit, dispatch }, column) {
    commit(types.SET_BILLABLE_ENQUIRY_DATE_COLUMN, column);
    return dispatch('getBillableEnquiries');
  },
  setBillableEnquirySearch({ commit }, search) {
    commit(types.SET_BILLABLE_ENQUIRY_SEARCH, search);
  },
  setBillableEnquiryStatus({ commit, dispatch }, status) {
    commit(types.SET_BILLABLE_ENQUIRY_STATUS, status);
    return dispatch('getBillableEnquiries', true);
  },
  setBillableEnquiryCurrentPage({ commit, dispatch }, page) {
    commit(types.SET_BILLABLE_ENQUIRIES_CURRENT_PAGE, page);
    return dispatch('getBillableEnquiries');
  },
  setBillableEnquiryPageSize({ commit, dispatch }, pageSize) {
    commit(types.SET_BILLABLE_ENQUIRIES_PAGE_SIZE, pageSize);
    return dispatch('getBillableEnquiries');
  },
  setBillableEnquirySortOrder({ commit, dispatch }, options) {
    commit(types.SET_BILLABLE_SORT_ORDER, options);
    return dispatch('getBillableEnquiries');
  },

  updateBill({ commit }, { id, updates }) {
    return bills
      .updateBill(id, updates)
      .then(response => {
        commit(types.SET_UPDATE_BILL, response.data);
        return response.data;
      })
      .catch(error => {
        console.log(error);
        throw error;
      });
  },
  addBill({ commit }, bill) {
    return bills
      .addBill(bill)
      .then(response => {
        commit(types.ADD_BILL_TO_BILLABLE_ENQUIRY, response.data);
        return response.data;
      })
      .catch(error => {
        console.log(error);
        throw error;
      });
  }
};

const mutations = {
  [types.ADD_BILL_TO_BILLABLE_ENQUIRY](state, bill) {
    const enquiryRecord = _.find(state.billable.enquiries, enquiry => enquiry.id === bill.enquiry_id);
    enquiryRecord.bills.push(bill);
  },
  [types.SET_BILLABLE_ENQUIRIES](state, response) {
    state.billable.enquiries = _.map(response.hits, '_source');
    state.billable.total = response.total;
    state.billable.lastPage = Math.ceil(state.billable.total / state.billable.pageSize);
  },
  [types.APPEND_BILLABLE_ENQUIRIES](state, response) {
    state.billable.enquiries = [...state.billable.enquiries, ..._.map(response.hits, '_source')];
  },
  [types.SET_BILLABLE_ENQUIRIES_CURRENT_PAGE](state, page) {
    state.billable.currentPage = page;
  },
  [types.SET_BILLABLE_ENQUIRIES_PAGE_SIZE](state, pageSize) {
    state.billable.pageSize = pageSize;
  },
  [types.RESET_BILLABLE_ENQUIRIES_TOTAL](state) {
    state.billable.total = null;
  },
  [types.SET_BILLABLE_ENQUIRY_FROM_DATE](state, from) {
    state.billable.filters.dates.from = from;
  },
  [types.SET_BILLABLE_ENQUIRY_TO_DATE](state, to) {
    state.billable.filters.dates.to = to;
  },
  [types.SET_BILLABLE_ENQUIRY_DATE_COLUMN](state, column) {
    state.billable.filters.dates.column = column;
  },
  [types.SET_BILLABLE_ENQUIRY_SEARCH](state, search) {
    state.billable.search = search;
  },
  [types.SET_BILLABLE_ENQUIRY_STATUS](state, status) {
    state.billable.filters.status = status;
  },
  [types.SET_BILLABLE_LOADING_STATE](state, value) {
    state.billable.isLoading = value;
  },
  [types.SET_BILLABLE_SORT_ORDER](state, { orderBy, orderDirection }) {
    state.billable.orderBy = orderBy;
    state.billable.orderDirection = orderDirection;
  },

  [types.SET_UPDATE_BILL](state, updatedBill) {
    const enquiryRecord = _.find(state.billable.enquiries, enquiry => enquiry.id === updatedBill.enquiry_id);
    const record = _.find(enquiryRecord.bills, bill => bill.id === updatedBill.id);
    record.type = updatedBill.type;
    record.fee = updatedBill.fee;
    record.bill_date = updatedBill.bill_date ? moment(updatedBill.bill_date).format(DATE_FORMAT) : null;
    record.received_date = updatedBill.received_date ? moment(updatedBill.received_date).format(DATE_FORMAT) : null;
    record.registered_company_to_bill = updatedBill.registered_company_to_bill;
    record.type_of_fee = updatedBill.type_of_fee;
    record.comments = updatedBill.comments;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
